import { useState, useRef } from 'react'
import { leadsAPI } from './api'
import { useLatest } from '../Bruktbil/useLatest'

/**
 * The caller's Mine Leads bookmarks as a set of org numbers, with an
 * optimistic toggle that rolls back when the request fails. A bookmark is
 * per organisation, so every row for the same org shares one star.
 */
export function useBookmarks() {
  const [bookmarked, setBookmarked] = useState<Set<string>>(new Set())
  // Toggles made before the list arrived. The list was requested before them,
  // so without replaying them on top it would silently undo those clicks.
  const pending = useRef(new Map<string, boolean>())

  // useLatest, not useEffect: StrictMode mounts twice in dev, and the first
  // request's list — just as stale — would land after the second had replayed
  // and cleared the pending clicks, undoing them (#638).
  useLatest(alive => {
    leadsAPI.getBookmarks()
      .then(res => {
        if (!alive()) return
        const next = new Set(res.data.map(b => b.org_nr))
        pending.current.forEach((on, orgNr) => { if (on) next.add(orgNr); else next.delete(orgNr) })
        pending.current = new Map()
        setBookmarked(next)
      })
      .catch(() => {})
  }, [])

  function flip(orgNr: string, on: boolean) {
    pending.current.set(orgNr, on)
    setBookmarked(prev => {
      const next = new Set(prev)
      if (on) next.add(orgNr)
      else next.delete(orgNr)
      return next
    })
  }

  async function toggle(orgNr: string) {
    const isSet = bookmarked.has(orgNr)
    flip(orgNr, !isSet)
    try {
      if (isSet) await leadsAPI.removeBookmark(orgNr)
      else await leadsAPI.addBookmark(orgNr)
    } catch {
      flip(orgNr, isSet)
    }
  }

  return { isBookmarked: (orgNr: string) => bookmarked.has(orgNr), toggle }
}
